// FileUploader.jsx
import React, { useState } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import { toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";

// ✅ headerValidation lives in the same folder as this file
import { validateHeadersFromFile, headerGuidance } from "./headerValidation";
import { acquireAccessToken } from "./import/importApi";

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

export default function FileUploader({
	setFileName,
	setCsvResult,
	setIsLoading,
	red_threshold,
	yellow_threshold,
	threshold = 0.7, // sensible default for "new lead" cutoff
	onStart, // optional: fired right as the scrub begins (opens the waiting room)
	onError, // optional: fired on any failure (closes the waiting room)
}) {
	const [files, setFiles] = useState([]);
	const { instance, accounts } = useMsal();

	// 🔧 Map backend rows -> UI shape (adds domain fields)
	const normalizeMatches = (rows = []) =>
		rows.map((r) => {
			const existingRaw = {
				lead_id: r.existing_lead_id ?? r.existing_id ?? "",
				company_name: r.existing_company_name ?? "",
				first_name: r.existing_first_name ?? "",
				last_name: r.existing_last_name ?? "",
				email: r.existing_email ?? "",
				normalized_phone:
					r.existing_normalized_phone ?? r.existing_phone_number ?? "",
				address: r.existing_address ?? "",
				city: r.existing_city ?? "",
			};

			const hasExisting = Object.values(existingRaw).some(
				(v) => v && String(v).trim() !== ""
			);

			const newLead = {
				company_name: r.new_company_name ?? "",
				first_name: r.new_first_name ?? "",
				last_name: r.new_last_name ?? "",
				email: r.new_email ?? "",
				normalized_phone:
					r.new_normalized_phone ??
					r.new_phone_number ?? // older payloads
					r.new_phone ??
					"", // raw phone fallback
				address: r.new_address ?? "",
				city: r.new_city ?? "",
			};

			// 🟢 Domain support (pair-level)
			// We accept multiple possible keys to be resilient to backend naming:
			// - r.matched_domain / r.domain / r.domain_matched
			// - r.domain_found (boolean) OR derive from matched_domain
			const matchedDomainRaw =
				r.matched_domain ?? r.domain ?? r.domain_matched ?? "";
			const matched_domain = String(matchedDomainRaw || "").trim() || "";
			const domain_found =
				typeof r.domain_found === "boolean"
					? r.domain_found
					: Boolean(matched_domain);

			return {
				decision: r.decision,
				match_score: Number(r.overall_score ?? 0),
				best_db_index: r.best_db_index ?? null,

				// pair-level domain info exposed to the UI
				matched_domain,
				domain_found,

				existing: hasExisting ? existingRaw : {},
				new: newLead,
			};
		});

	// -------- Firefox-friendly CSV validation ----------
	// Coerce odd/empty MIME types to 'text/csv' if extension or content looks like CSV.
	const fileValidateTypeDetectType = (source, type) =>
		new Promise(async (resolve) => {
			const name = (source && "name" in source && source.name) || "";
			const lower = String(name).toLowerCase();
			const isCsvExt = lower.endsWith(".csv");

			if (type === "text/csv") return resolve(type);

			const csvish = [
				"application/vnd.ms-excel",
				"application/csv",
				"text/plain",
				"",
			];
			if (csvish.includes(type)) return resolve("text/csv");

			if ((!type || type === "application/octet-stream") && isCsvExt)
				return resolve("text/csv");

			try {
				const head = await source.slice(0, 4096).text();
				const hasLines = /\r?\n/.test(head);
				const delimCount =
					(head.match(/,/g) || []).length +
					(head.match(/;/g) || []).length +
					(head.match(/\t/g) || []).length;
				if (hasLines && delimCount > 0) return resolve("text/csv");
			} catch {
				/* ignore */
			}
			return resolve(type);
		});

	const handleProcess = async (
		fieldName,
		file,
		metadata,
		load,
		error,
		progress,
		abort
	) => {
		if (!file.name.toLowerCase().endsWith(".csv")) {
			toast.error("❌ Only .csv files are allowed.");
			error("Only CSV allowed");
			return abort();
		}

		// 🔍 Preflight header validation BEFORE uploading to the backend
		try {
			const { ok, problems, suggestions } = await validateHeadersFromFile(file);
			if (!ok) {
				const msg = [
					"⚠️ Your CSV headers need attention.",
					...(problems || []),
					"",
					headerGuidance(),
				]
					.filter(Boolean)
					.join("\n");
				toast.error(msg, {
					autoClose: 10000,
					style: { whiteSpace: "pre-wrap" },
				});
				error("Invalid headers");
				return abort();
			}
			if (suggestions) {
				toast.warn(suggestions, {
					autoClose: 9000,
					style: { whiteSpace: "pre-wrap" },
				});
			}
		} catch (e) {
			console.error(e);
			toast.error(
				"❌ Could not read CSV headers. Make sure it’s a valid CSV.",
				{
					autoClose: 8000,
				}
			);
			error("Header read failed");
			return abort();
		}

		setFileName(file.name);
		setIsLoading(true);
		onStart?.();

		const formData = new FormData();
		formData.append("file", file);
		formData.append("threshold", String(threshold));
		formData.append("red_threshold", String(red_threshold));
		formData.append("yellow_threshold", String(yellow_threshold));

		try {
			// The backend requires a signed-in SFP account and emails the
			// results to the address in the token.
			const token = await acquireAccessToken(instance, accounts[0]);
			const response = await fetch("/api/match-leads", {
				method: "POST",
				headers: { Authorization: `Bearer ${token}` },
				body: formData,
			});

			if (!response.ok) {
				const errorDetails = await response.text();
				toast.error(`❌ Upload failed: ${errorDetails}`, { autoClose: 8000 });
				onError?.(errorDetails);
				error("Upload failed");
				return;
			}

			const result = await response.json();

			// ✅ Normalize & include domain data so UI components can render badges/rows
			const normalized = normalizeMatches(result.matches || []);
			const finalResult = { ...result, matches: normalized };

			setCsvResult(finalResult);
			console.log(result);
			console.log(finalResult);

			const n = result.notification;
			const msg =
				typeof n === "string"
					? n
					: n?.summary
					? `${n.summary} • HIGH ${n?.counts?.HIGH ?? 0} • MED ${
							n?.counts?.MEDIUM ?? 0
					  } • LOW ${n?.counts?.LOW ?? 0}`
					: "✅ Match completed and ZIP ready to download!";
			toast.success(msg, { autoClose: 5000 });

			load(file.name);
		} catch (err) {
			console.error(err);
			toast.error(`❌ Failed to process file: ${err.message}`, {
				autoClose: 8000,
			});
			onError?.(err.message);
			error("Error uploading");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div
			style={{ marginTop: 30 }}
			className={`${files.length > 0 ? "uploading" : ""}`}>
			<h1>📂 Upload Lead Matching Data</h1>
			<p style={{ marginBottom: 10 }}>
				Drag a CSV file here or click to browse:
			</p>

			{/* Accept common CSV-ish MIME types that Firefox/Windows uses.
          Tell FilePond how to “detect” CSV when browsers leave MIME blank or odd. */}
			<FilePond
				files={files}
				onupdatefiles={setFiles}
				className="large-uploader"
				allowMultiple={false}
				acceptedFileTypes={[
					"text/csv",
					".csv",
					"application/vnd.ms-excel",
					"application/csv",
					"text/plain",
				]}
				fileValidateTypeDetectType={fileValidateTypeDetectType}
				fileValidateTypeLabelExpectedTypesMap={{
					"text/csv": ".csv",
					"application/vnd.ms-excel": ".csv",
					"application/csv": ".csv",
					"text/plain": ".csv",
				}}
				allowFileTypeValidation={true}
				allowFileSizeValidation={true}
				maxFileSize="25MB"
				labelIdle='📂 Drag & Drop your CSV or <span class="filepond--label-action">Browse</span>'
				labelProcessing="🔄 Matching in progress…"
				labelFileProcessing="Matching in progress…"
				labelFileProcessingComplete="✅ Match completed and ZIP ready to download!"
				labelFileProcessingAborted="Matching aborted"
				labelFileProcessingError="❌ Match failed"
				server={{ process: handleProcess }}
				instantUpload={true}
			/>
		</div>
	);
}
